/**
 * Driving a multipart upload, without the browser plumbing.
 *
 * R2 refuses a single PUT over ~4.995 GiB — and refuses it at the END of the
 * transfer, so a 9GB match video uploaded for forty minutes and then failed
 * with a 400 nobody could read. Anything that size goes up in parts.
 *
 * The transport is injected rather than imported: the sequencing, the
 * absolute progress accounting and the per-part retry are the parts that can
 * be wrong, and none of them need an XMLHttpRequest to be tested.
 */

export type PartPlan = {
  jobId: string
  partSize: number
  totalParts: number
}

export type UploadedPart = { part_number: number; etag: string }

export type MultipartDeps = {
  /** A fresh presigned URL for one part. Called per ATTEMPT, not per part:
   *  the URL lives an hour and a slow upload can outlast it. */
  presignPart: (jobId: string, partNumber: number) => Promise<string>
  /** PUT one part, resolving with the ETag response header when the browser
   *  is allowed to read it. `onLoaded` reports bytes sent FOR THIS PART. */
  put: (url: string, blob: Blob, onLoaded: (loaded: number) => void) => Promise<string | null>
  /** Finish the upload. An empty `parts` means the ETags were unreadable and
   *  the server should ask R2 for the list itself. */
  complete: (jobId: string, parts: UploadedPart[]) => Promise<void>
  /** Bytes sent for the WHOLE file, so one progress bar covers all parts. */
  onProgress: (loaded: number) => void
  sleep?: (ms: number) => Promise<void>
}

/** Thrown by `put` when the person cancelled; never retried. */
export const CANCELED = 'Upload canceled'

const ATTEMPTS = 3

export async function uploadInParts(
  file: Blob, plan: PartPlan, deps: MultipartDeps,
): Promise<UploadedPart[]> {
  const sleep = deps.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)))
  const parts: UploadedPart[] = []
  let done = 0

  for (let n = 1; n <= plan.totalParts; n++) {
    const start = (n - 1) * plan.partSize
    const blob = file.slice(start, Math.min(start + plan.partSize, file.size))
    const base = done

    let lastErr: unknown = null
    for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
      try {
        const url = await deps.presignPart(plan.jobId, n)
        const etag = await deps.put(url, blob, (loaded) => deps.onProgress(base + loaded))
        // Quotes are part of the header, not of the ETag.
        if (etag) parts.push({ part_number: n, etag: etag.replace(/"/g, '') })
        lastErr = null
        break
      } catch (e) {
        lastErr = e
        // A cancel is a decision, not a failure to work around.
        if (e instanceof Error && e.message === CANCELED) throw e
        if (attempt < ATTEMPTS - 1) await sleep(1000 * (attempt + 1))
      }
    }
    // One dropped connection anywhere in forty minutes used to throw the
    // whole transfer away; now it costs one part.
    if (lastErr) throw lastErr

    done += blob.size
    deps.onProgress(done)
  }

  await deps.complete(plan.jobId, parts)
  return parts
}
